import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import type { HelpStartersRecord } from '../types/gotypes'

interface ListStartersResponse {
  starters: HelpStartersRecord[]
  count: number
}

/**
 * A customer's starter questions (19 Oct 2026): one list per app section,
 * replacing the section's shipped `starters` (starterQuestionsFor). Read by
 * anyone who holds the customer (the page helpers and the Help pane show them
 * as pills); written by Managers on Admin → Management → System context.
 * Each list carries the section's opening line (`intro`; '' = the shipped
 * one, sectionIntroFor). Reset deletes the row, and the shipped questions and
 * line apply again.
 */
export const useHelpStarters = ({ customer, enabled = true }: { customer: string; enabled?: boolean }) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const listKey = ['help-starters', customer]

  const listQuery = useQuery({
    queryKey: listKey,
    enabled: Boolean(enabled && customer && account.user),
    queryFn: async () => {
      try {
        const res = await axios.get<ListStartersResponse>(`${API_BASE_URL}/help/${customer}/starters`)
        return res.data.starters ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKey })

  const saveMutation = useMutation({
    // intro absent: the server keeps the stored opening line; '' clears it.
    mutationFn: async ({ section, questions, intro }: { section: string; questions: string[]; intro?: string }) =>
      (await axios.put<HelpStartersRecord>(`${API_BASE_URL}/help/${customer}/starters/${section}`, intro === undefined ? { questions } : { questions, intro })).data,
    onSuccess: () => { invalidate(); snackbar.success('Opening line and starter questions saved') },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const resetMutation = useMutation({
    mutationFn: async ({ section }: { section: string }) => {
      await axios.delete(`${API_BASE_URL}/help/${customer}/starters/${section}`)
    },
    onSuccess: () => { invalidate(); snackbar.success('Reset: the shipped opening line and questions apply') },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  return {
    records: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error ? listQuery.error.message : null,
    handleSave: saveMutation.mutateAsync,
    handleReset: resetMutation.mutateAsync,
    isSaving: saveMutation.isPending || resetMutation.isPending,
  }
}

export default useHelpStarters
